import React from 'react';
import { CalendarBlank, Star, User, Clock, CheckCircle, Hourglass, ArrowRight } from '@phosphor-icons/react';

interface ReviewCycleItem {
    id: string | number;
    cycleId: string | number;
    cycleName: string;
    cycleTitle?: string;
    cycleType: string;
    templateName?: string;
    status: string;
    flowStatus: string;
    reviewType?: string;
    avgManagerRating?: number;
    managerName?: string;
    createdDate?: string;
    reviewStartedDate?: string;
    reviewDueDate?: string;
    reviewCompletedDate?: string;
    cycleStartDate?: string;
    cycleEndDate?: string;
    value?: number;
    // Support snake_case for backwards compatibility
    cycle_id?: string | number;
    cycle_name?: string;
    cycle_title?: string;
    cycle_type?: string;
    template_name?: string;
    flow_status?: string;
    review_type?: string;
    avg_manager_rating?: number;
    manager_name?: string;
    created_date?: string;
    review_started_date?: string;
    review_due_date?: string;
    review_completed_date?: string;
    cycle_start_date?: string;
    cycle_end_date?: string;
}

interface ReviewCycleCardProps {
    data: ReviewCycleItem[] | { data: ReviewCycleItem[]; metadata?: any; meta?: any; schema?: any };
    options?: {
        show_rating?: boolean;
        show_manager?: boolean;
        show_dates?: boolean;
        max_items?: number;
    };
}

const getFlowStatusConfig = (status: string) => {
    const s = (status || '').toLowerCase().replace(/_/g, ' ');

    // Completed states
    if (s.includes('complete')) {
        return { color: 'bg-green-100 text-green-700 border border-green-200', label: 'Completed', icon: CheckCircle };
    }
    // In progress states
    if (s.includes('progress') || s.includes('ongoing') || s.includes('started')) {
        return { color: 'bg-blue-100 text-blue-700 border border-blue-200', label: 'In Progress', icon: ArrowRight };
    }
    // Assigned state
    if (s.includes('assign') && !s.includes('pending')) {
        return { color: 'bg-indigo-100 text-indigo-700 border border-indigo-200', label: 'Assigned', icon: User };
    }
    // Pending states (nomination, nominee, response, etc.)
    if (s.includes('pending') || s.includes('waiting') || s.includes('new')) {
        return { color: 'bg-amber-100 text-amber-700 border border-amber-200', label: formatStatusLabel(s), icon: Hourglass };
    }
    // Default
    return { color: 'bg-gray-100 text-gray-600 border border-gray-200', label: formatStatusLabel(s), icon: Clock };
};

const formatStatusLabel = (status: string): string => {
    // Clean up and capitalize status
    return status
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const getCycleTypeConfig = (type: string) => {
    const t = (type || '').toLowerCase();

    if (t === 'standard' || t.includes('performance')) {
        return { color: 'bg-violet-100 text-violet-700', label: 'Performance' };
    }
    if (t === '360' || t.includes('360')) {
        return { color: 'bg-cyan-100 text-cyan-700', label: '360 Review' };
    }
    if (t === 'check in' || t.includes('check')) {
        return { color: 'bg-teal-100 text-teal-700', label: 'Check-In' };
    }
    if (t.includes('annual') || t.includes('yearly')) {
        return { color: 'bg-purple-100 text-purple-700', label: 'Annual' };
    }
    if (t.includes('quarterly')) {
        return { color: 'bg-blue-100 text-blue-700', label: 'Quarterly' };
    }
    if (t.includes('probation')) {
        return { color: 'bg-orange-100 text-orange-700', label: 'Probation' };
    }
    if (t.includes('pip')) {
        return { color: 'bg-red-100 text-red-700', label: 'PIP' };
    }
    return { color: 'bg-gray-100 text-gray-600', label: type || 'Review' };
};

const formatDate = (dateStr?: string | null): string | null => {
    if (!dateStr) return null;
    try {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return null;
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    } catch {
        return null;
    }
};

const isOverdue = (dueDateStr?: string | null, completedDateStr?: string | null): boolean => {
    if (completedDateStr) return false; // Not overdue if completed
    if (!dueDateStr) return false;
    try {
        const dueDate = new Date(dueDateStr);
        if (isNaN(dueDate.getTime())) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return dueDate < today;
    } catch {
        return false;
    }
};

const renderRating = (rating?: number) => {
    if (rating === undefined || rating === null || rating === 0) return null;
    const roundedRating = Math.round(rating * 10) / 10;
    const fullStars = Math.floor(roundedRating);
    const hasHalfStar = roundedRating - fullStars >= 0.5;

    return (
        <div className="flex items-center gap-1.5 bg-amber-50 px-2 py-1 rounded-lg border border-amber-100">
            <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                    <Star
                        key={i}
                        size={12}
                        weight={i < fullStars ? 'fill' : (i === fullStars && hasHalfStar ? 'duotone' : 'regular')}
                        className={i < fullStars || (i === fullStars && hasHalfStar) ? 'text-amber-500' : 'text-gray-300'}
                    />
                ))}
            </div>
            <span className="text-xs font-bold text-amber-700">{roundedRating.toFixed(1)}</span>
        </div>
    );
};

export const ReviewCycleCard: React.FC<ReviewCycleCardProps> = ({ data, options = {} }) => {
    const {
        show_rating = true,
        show_manager = true,
        show_dates = true,
        max_items = 20
    } = options;

    // Handle various data wrapper formats from the API
    let items: ReviewCycleItem[] = [];
    if (Array.isArray(data)) {
        items = data;
    } else if (data && typeof data === 'object') {
        if ('data' in data && Array.isArray(data.data)) {
            items = data.data;
        }
    }

    const displayItems = items.slice(0, max_items);

    if (!displayItems || displayItems.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 p-6">
                <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                    <CalendarBlank size={28} className="text-gray-400" />
                </div>
                <span className="text-sm font-medium">No review cycles found</span>
                <span className="text-xs text-gray-400 mt-1">Your review history will appear here</span>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2.5 p-3 h-full overflow-auto">
            {displayItems.map((item, index) => {
                // Support both camelCase (from analyticsService) and snake_case (legacy)
                const itemFlowStatus = item.flowStatus || item.flow_status || '';
                const itemCycleType = item.cycleType || item.cycle_type || '';
                // Support cycle_title as fallback for cycle_name (API may return either)
                const itemCycleName = item.cycleName || item.cycle_name || item.cycleTitle || item.cycle_title || '';
                const itemReviewType = item.reviewType || item.review_type || '';
                const itemManagerName = item.managerName || item.manager_name || '';
                const itemAvgManagerRating = item.avgManagerRating ?? item.avg_manager_rating;
                const itemReviewDueDate = item.reviewDueDate || item.review_due_date;
                const itemReviewCompletedDate = item.reviewCompletedDate || item.review_completed_date;

                const flowStatus = getFlowStatusConfig(itemFlowStatus);
                const cycleType = getCycleTypeConfig(itemCycleType);
                const StatusIcon = flowStatus.icon;
                const dueDate = formatDate(itemReviewDueDate);
                const completedDate = formatDate(itemReviewCompletedDate);
                const overdue = isOverdue(itemReviewDueDate, itemReviewCompletedDate);
                const isCompleted = itemFlowStatus.toLowerCase().includes('complete');

                return (
                    <div
                        key={`review-${item.id}-${index}`}
                        className="group bg-white rounded-lg border border-gray-200 p-3.5 hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer"
                    >
                        {/* Row 1: Cycle Name + Rating */}
                        <div className="flex items-start justify-between gap-3 mb-2.5">
                            <h3 className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2 group-hover:text-blue-600 transition-colors flex-1">
                                {itemCycleName || 'Unnamed Cycle'}
                            </h3>
                            {show_rating && renderRating(itemAvgManagerRating)}
                        </div>

                        {/* Row 2: Badges */}
                        <div className="flex items-center gap-2 flex-wrap mb-3">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${cycleType.color}`}>
                                {cycleType.label}
                            </span>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${flowStatus.color}`}>
                                <StatusIcon size={10} weight="bold" />
                                {flowStatus.label}
                            </span>
                            {itemReviewType && itemReviewType !== 'Primary' && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-gray-100 text-gray-600">
                                    {itemReviewType}
                                </span>
                            )}
                        </div>

                        {/* Row 3: Manager + Date */}
                        <div className="flex items-center justify-between text-xs text-gray-500 pt-2.5 border-t border-gray-100">
                            {show_manager && itemManagerName ? (
                                <div className="flex items-center gap-1.5 min-w-0">
                                    <User size={14} className="text-gray-400 flex-shrink-0" />
                                    <span className="truncate">{itemManagerName}</span>
                                </div>
                            ) : (
                                <div />
                            )}

                            {show_dates && (
                                <div className={`flex items-center gap-1.5 flex-shrink-0 ${
                                    overdue ? 'text-red-600 font-medium' :
                                    isCompleted ? 'text-green-600' : ''
                                }`}>
                                    {isCompleted && completedDate ? (
                                        <>
                                            <CheckCircle size={14} weight="fill" className="text-green-500" />
                                            <span>{completedDate}</span>
                                        </>
                                    ) : dueDate ? (
                                        <>
                                            <Clock size={14} className={overdue ? 'text-red-500' : ''} />
                                            <span>{overdue ? 'Overdue: ' : 'Due: '}{dueDate}</span>
                                        </>
                                    ) : null}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
